/**
 * Credit Plans & Costs (frontend view)
 * ====================================
 *
 * Mirror of `functions/creditCosts.js`. The backend is authoritative — this
 * module exists so the UI can price an action BEFORE calling a function and
 * show the user what their balance is worth. Never trust it for enforcement.
 *
 * Credit prices are derived, not hand-written:
 *
 *     credits = ceilQuantized(eurCost * vendorSurcharge / anchorEurPerCredit)
 *
 * `anchorEurPerCredit` (0.005) means one credit covers half a cent of real
 * AI/vendor spend. Plan allowances are sized against the same anchor, so a
 * fully drained plan can never cost more than 25% of the subscription price.
 * See `functions/creditCosts.js` for the full rationale.
 *
 * The data file is a byte-identical copy of `functions/creditCosts.json`
 * (Create React App forbids importing from outside `src/`). Verify with
 * `npm run check:credits`.
 */

import DATA from './creditCosts.json';

const { anchorEurPerCredit, maxCostShareOfRevenue, usdToEur, rolloverRatio } = DATA.economics;

/** Visual identity per plan — presentation only, never affects pricing. */
const PLAN_THEME = {
  starter: {
    color: '#6897B7',
    gradient: 'linear-gradient(135deg, #6897B7 0%, lightblue 100%)',
    bgGradient: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    academy: 'standard',
  },
  pro: {
    color: '#2a6776',
    gradient: 'linear-gradient(45deg, #2a6776 0%, #6da7ed 100%)',
    bgGradient: 'linear-gradient(135deg, #e8f4f8 0%, #d1ecf1 100%)',
    academy: 'standard',
  },
  enterprise: {
    color: '#6da7ed',
    gradient: 'linear-gradient(135deg, #6da7ed 0%, #2a6776 100%)',
    bgGradient: 'linear-gradient(135deg, #eef5fc 0%, #dce8f5 100%)',
    academy: 'premium',
  },
};

/**
 * Quantize a raw credit amount UP to a clean, presentable step. Must stay
 * identical to the backend implementation.
 */
const quantizeCredits = (raw) => {
  const value = Number(raw) || 0;
  if (value <= 0) return 0;
  const step = value < 1 ? 0.05 : value < 10 ? 0.25 : 0.5;
  const stepped = Math.ceil(value / step - 1e-9) * step;
  return Math.round(stepped * 100) / 100;
};

const creditsFromEur = (eur) => quantizeCredits((Number(eur) || 0) / anchorEurPerCredit);
const eurFromCredits = (credits) => (Number(credits) || 0) * anchorEurPerCredit;
const roundCredits = (value) => Math.round((Number(value) || 0) * 100) / 100;

const entryCredits = (entry) =>
  creditsFromEur((Number(entry.eurCost) || 0) * (Number(entry.vendorSurcharge) || 1));

const buildCostMap = (section) =>
  Object.entries(section).reduce((acc, [key, entry]) => {
    acc[key] = {
      cost: entryCredits(entry),
      label: entry.label,
      eurCost: (Number(entry.eurCost) || 0) * (Number(entry.vendorSurcharge) || 1),
      tier: entry.tier,
    };
    return acc;
  }, {});

const CREDIT_COSTS = buildCostMap(DATA.actions);
const SPY_MODULE_COSTS = buildCostMap(DATA.spyModules);
const WEBSITE_ANALYZER_MODULE_COSTS = buildCostMap(DATA.websiteAnalyzerModules);

/** Flat `{ module: credits }` maps — kept for call sites that index directly. */
const SPY_MODULE_CREDITS = Object.entries(SPY_MODULE_COSTS).reduce((acc, [k, v]) => {
  acc[k] = v.cost;
  return acc;
}, {});

const WEBSITE_ANALYZER_MODULE_CREDITS = Object.entries(WEBSITE_ANALYZER_MODULE_COSTS).reduce(
  (acc, [k, v]) => {
    acc[k] = v.cost;
    return acc;
  },
  {}
);

const CREDIT_PLANS = Object.entries(DATA.plans).reduce((acc, [id, plan]) => {
  const theme = PLAN_THEME[id] || {};
  acc[id] = {
    id,
    label: plan.label,
    priceEur: plan.priceEur,
    monthlyCredits: plan.monthlyCredits,
    emailPlan: plan.emailPlan,
    color: theme.color,
    gradient: theme.gradient,
    bgGradient: theme.bgGradient,
    features: {
      emailsPerDay: plan.limits.dailyEmailLimit,
      preparedQueueCap: plan.limits.preparedQueueCap || 60,
      leadsPerMonth: plan.limits.leadsPerMonth,
      deepScansPerMonth: plan.limits.deepScansPerMonth,
      teamMembers: plan.limits.teamMembers,
      emailAccounts: plan.limits.emailAccounts,
      hyperIntelligence: true,
      academy: theme.academy,
    },
  };
  return acc;
}, {});

const PLAN_IDS = Object.keys(CREDIT_PLANS);

/** `emailPlan` (standard/premium/unlimited) -> daily send limit. */
const EMAIL_PLAN_LIMITS = PLAN_IDS.reduce((acc, id) => {
  acc[CREDIT_PLANS[id].emailPlan] = CREDIT_PLANS[id].features.emailsPerDay;
  return acc;
}, {});

/** `emailPlan` string -> plan id. Falls back to the entry tier. */
const planIdForEmailPlan = (emailPlan) =>
  PLAN_IDS.find((id) => CREDIT_PLANS[id].emailPlan === emailPlan) || PLAN_IDS[0];

/**
 * Active SalesGod membership only. Never infer Starter/Pro/Enterprise from
 * the leftover `emailPlan` / `isPremium` flags — those used to be toggled
 * independently in the Team Dashboard and drifted from Memberships.
 */
const PLAN_ID_ALIASES = { standard: 'starter', premium: 'pro', unlimited: 'enterprise' };

/** Same label and colour as the account badge in the nav. Enterprise reads as Max. */
const PLAN_NAV_BADGE = {
  starter: { label: 'Starter', kind: 'starter' },
  pro: { label: 'Pro', kind: 'pro' },
  enterprise: { label: 'Max', kind: 'max' },
};

const normalizePlanId = (raw) => {
  if (!raw) return null;
  if (CREDIT_PLANS[raw]) return raw;
  return PLAN_ID_ALIASES[raw] || null;
};

const PREPARED_QUEUE_CAP_DEFAULT = 60;

const QUALITY_CHECKS_PER_MONTH_DEFAULT = 4;

const qualityChecksPerMonth = (planId) => {
  const plan = DATA.plans[planId];
  const n = plan && plan.limits && Number(plan.limits.qualityChecksPerMonth);
  return n > 0 ? n : QUALITY_CHECKS_PER_MONTH_DEFAULT;
};

const preparedQueueCapForPlan = (planId) => {
  const cap = CREDIT_PLANS[planId] && CREDIT_PLANS[planId].features.preparedQueueCap;
  return Number(cap) > 0 ? Number(cap) : PREPARED_QUEUE_CAP_DEFAULT;
};

const resolveMembershipPlanId = (creditPlan, userData) => {
  // Older records may omit `isActive`. Only an explicit false means deactivated.
  if (creditPlan && creditPlan.isActive !== false) {
    const fromPlan = normalizePlanId(creditPlan.plan);
    if (fromPlan) return fromPlan;
  }
  return normalizePlanId(userData?.membershipPlan);
};

const getPlanCredits = (planId) => (CREDIT_PLANS[planId] ? CREDIT_PLANS[planId].monthlyCredits : 0);

/** Credit price of a single action key. Unknown keys cost 0. */
const costOf = (actionKey) => (CREDIT_COSTS[actionKey] ? CREDIT_COSTS[actionKey].cost : 0);

/**
 * Spy / Web-Research price: one base Grok web-search call is always charged,
 * plus one extra call per "heavy" module. Light modules read from the base
 * call's output and only add a token sliver.
 */
const calculateSpyCost = (activeModules) => {
  const base = SPY_MODULE_COSTS.core.cost;
  if (!Array.isArray(activeModules)) return base;
  return roundCredits(
    activeModules.reduce((total, mod) => {
      const entry = SPY_MODULE_COSTS[mod];
      if (!entry || mod === 'core') return total;
      return total + entry.cost;
    }, base)
  );
};

/** Website Analyzer price: one call covers every module. */
const calculateWebsiteAnalyzerCost = (activeModules) => {
  const base = costOf('websiteAnalyzer');
  if (!Array.isArray(activeModules)) return base;
  return roundCredits(
    activeModules.reduce((total, mod) => {
      const entry = WEBSITE_ANALYZER_MODULE_COSTS[mod];
      return entry ? total + entry.cost : total;
    }, base)
  );
};

/**
 * Credit price of generating ONE email body. Must match
 * `functions/creditCosts.js -> emailGenerationCost()`.
 *
 * `ultraMode` is deliberately NOT a separate tier: it runs the same Claude
 * Sonnet writer as regular Hyper-Intelligence, so it costs the same.
 */
const emailGenerationCost = ({
  hyperIntelligence = false,
  hasReferenceFile = false,
  voiceCloneTier = null,
} = {}) => {
  if (voiceCloneTier) {
    const tier = String(voiceCloneTier).toLowerCase();
    if (tier === 'good' || tier === 'basic' || tier === 'haiku') return costOf('voiceCloneGood');
    if (tier === 'ultra') return costOf('voiceCloneUltra');
    return costOf('voiceCloneBest');
  }
  if (hyperIntelligence) return costOf(hasReferenceFile ? 'emailHyperPdf' : 'emailHyper');
  return costOf('emailStandard');
};

const charsToTokens = (chars) => Math.ceil((Number(chars) || 0) / 4);

/**
 * Worst-case EUR cost of one generated image. Uses the UPPER bound of the
 * variable inputs (research context, input screenshot) because those are only
 * known per recipient at send time — and we charge before we know them.
 */
const imageCostEur = ({
  model,
  aspect = 'square',
  quality = 'medium',
  hasInputImage = false,
  promptChars = 0,
  sourceMode = null,
} = {}) => {
  const cfg = DATA.imageGeneration;
  const spec = cfg.models[model];
  if (!spec) return null;

  const aspectKey = cfg.aspectDims[aspect] ? aspect : 'square';
  let usd;

  if (spec.kind === 'flux') {
    const [w, h] = cfg.aspectDims[aspectKey];
    usd = ((w * h) / 1e6) * spec.usdPerMp;
  } else if (spec.kind === 'flat') {
    usd = spec.usd;
  } else {
    const q = ['low', 'medium', 'high'].includes(quality) ? quality : 'medium';
    usd = spec.usd[aspectKey][q];
  }

  let eur = usd * usdToEur;

  if (spec.tokenRates) {
    eur += cfg.tokenAllowanceEur;
    if (hasInputImage) eur += cfg.tokenAllowanceEur;
    eur += charsToTokens(promptChars) * spec.tokenRates.textIn * usdToEur;
  }

  if (sourceMode === 'website') eur += cfg.websiteScreenshotEur;
  // Photo mode crawls their site and lets a vision model pick the photo.
  // Website mode picks their best photo too, next to the screenshot.
  if (sourceMode === 'websitePhoto' || sourceMode === 'website') eur += cfg.websitePhotoPickEur;

  return eur;
};

/** Credit price of one generated image. Returns 0 for unknown models. */
const calculateImageCredits = (opts = {}) => {
  const eur = imageCostEur(opts);
  return eur == null ? 0 : creditsFromEur(eur);
};

/**
 * Translate a credit balance into "what can I still do with this".
 *
 * Powers the dashboard hint under the credit meter. Email actions include the
 * per-send delivery cost so the number matches what the user actually spends.
 *
 * `maxEmails` clamps the email rows to what the daily send limit still permits.
 * Cheap generations are not credit-bound at all — a Starter balance covers
 * ~17,000 standard emails while the plan only allows ~1,800 a month — and
 * printing the credit figure would promise capacity the user cannot use.
 *
 * Only generation is charged; delivery itself costs no credits and is capped by
 * the daily send quota instead.
 *
 * @param {number} balance current credit balance
 * @param {Object} [opts]
 * @param {number} [opts.maxEmails] remaining sends allowed this billing period
 */
const describeCreditCapacity = (balance, { maxEmails = null } = {}) => {
  const credits = Math.max(0, Number(balance) || 0);
  return DATA.dashboardCapabilities
    .map((cap) => {
      const unit = costOf(cap.key);
      if (!unit) return null;
      const byCredits = Math.floor(credits / unit);
      const capped = cap.isEmail && maxEmails != null;
      return {
        key: cap.key,
        label: cap.label,
        icon: cap.icon,
        creditsPerUnit: roundCredits(unit),
        possible: capped ? Math.min(byCredits, Math.max(0, maxEmails)) : byCredits,
        // True when the send limit — not the balance — is what stops the user.
        sendLimited: capped && maxEmails < byCredits,
      };
    })
    .filter(Boolean);
};

/** Modelled worst-case vendor spend of a fully drained plan, and its margin. */
const planBudget = (planId) => {
  const plan = CREDIT_PLANS[planId];
  if (!plan) return null;
  const maxSpendEur = plan.monthlyCredits * anchorEurPerCredit;
  const budgetEur = plan.priceEur * maxCostShareOfRevenue;
  return {
    planId,
    priceEur: plan.priceEur,
    monthlyCredits: plan.monthlyCredits,
    maxSpendEur,
    budgetEur,
    shareOfRevenue: maxSpendEur / plan.priceEur,
    headroomEur: budgetEur - maxSpendEur,
    withinBudget: maxSpendEur <= budgetEur,
  };
};

export {
  DATA as CREDIT_COST_DATA,
  CREDIT_PLANS,
  CREDIT_COSTS,
  SPY_MODULE_CREDITS,
  SPY_MODULE_COSTS,
  WEBSITE_ANALYZER_MODULE_CREDITS,
  WEBSITE_ANALYZER_MODULE_COSTS,
  EMAIL_PLAN_LIMITS,
  PREPARED_QUEUE_CAP_DEFAULT,
  preparedQueueCapForPlan,
  qualityChecksPerMonth,
  PLAN_IDS,
  PLAN_NAV_BADGE,
  planIdForEmailPlan,
  resolveMembershipPlanId,
  getPlanCredits,
  costOf,
  emailGenerationCost,
  calculateSpyCost,
  calculateWebsiteAnalyzerCost,
  calculateImageCredits,
  imageCostEur,
  describeCreditCapacity,
  quantizeCredits,
  creditsFromEur,
  eurFromCredits,
  roundCredits,
  planBudget,
  anchorEurPerCredit as ANCHOR_EUR_PER_CREDIT,
  maxCostShareOfRevenue as MAX_COST_SHARE_OF_REVENUE,
  rolloverRatio as ROLLOVER_RATIO,
};
